<script setup lang="ts">
  import { computed } from 'vue';

  import type { CreditMemo, Invoice, Partner } from '@stuut/api/models';
  import { StackedText } from '@ui/components';
  import { Shimmer } from '@ui/components/ai-elements/shimmer';
  import { Badge } from '@ui/components/badge';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { Skeleton } from '@ui/components/skeleton';

  import { useFeatureFlag } from '@/components/feature-flags';
  import { PartnerQuickView } from '@/components/partners';
  import type { EmailProcessingStep } from '@/types/emailProcessing';
  import { getProcessingLabel } from '@/types/emailProcessing';

  interface Props {
    creditMemos: CreditMemo[] | undefined;
    invoices: Invoice[] | undefined;
    isInternal?: boolean;
    /** True only while a partner that exists on the thread is still being fetched. */
    loading?: boolean;
    partner: Partner | undefined;
    processingStep?: EmailProcessingStep;
  }

  const props = defineProps<Props>();

  const processingLabel = computed(() => getProcessingLabel(props.processingStep));

  const { value: internalContactsUi } = useFeatureFlag('internal-contacts-ui');

  const maxInvoicesShown = 10;
  const maxCreditMemosShown = 10;
</script>

<template>
  <div class="px-2 flex flex-wrap">
    <div v-if="loading" class="flex justify-between gap-5 h-8.5 w-full">
      <Skeleton class="w-32 h-full" />
      <Skeleton class="w-64 h-full" />
    </div>

    <div v-else class="flex w-full justify-between gap-8">
      <StackedText size="sm" class="flex-none">
        <template #top>
          <div class="flex items-center gap-2">
            <PartnerQuickView v-if="partner" :partner="partner" class="text-left" />
            <Badge
              v-if="internalContactsUi && isInternal !== undefined"
              :variant="isInternal ? 'primary' : 'warning'"
              :style="!isInternal ? { color: 'var(--warning-yellow-900)' } : undefined"
            >
              {{ isInternal ? 'Internal Thread' : 'External Thread' }}
            </Badge>
          </div>
        </template>

        <template #bottom>
          <div v-if="processingStep" class="flex items-center gap-x-2">
            <LoadingSpinner class="text-sm far" />

            <Shimmer as="span" :duration="1.5" class="text-xs">
              {{ processingLabel }}
            </Shimmer>
          </div>

          <span v-else-if="partner" class="text-nowrap">Customer</span>
        </template>
      </StackedText>
    </div>
  </div>
</template>

<style scoped></style>
